import { DEFAULT_GENERATION_MODEL_KEY } from '@malaberg/shared';
import type {
  CreateGenerationQueueItemRequest,
  EntryOrFreeText,
  RunResponse,
  StructuredPremisePacket,
} from '@malaberg/shared';
import { Badge, Button, Card, Group, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { generationQueueApi } from '../../api/endpoints/generation-queue';
import { useGenerationQueueStore } from '../../state/generation-queue-store';
import { useRunStreamStore } from '../../state/run-stream-store';

interface PremisePacketCardsProps {
  run: RunResponse;
}

function toEphemeral(value: string): EntryOrFreeText {
  return { ephemeralValue: value };
}

function buildScratchDraftBrief(
  run: RunResponse,
  selectedPremise: StructuredPremisePacket
): CreateGenerationQueueItemRequest['brief'] {
  return {
    mode: 'scratch_ad_draft',
    generationModelKey: run.generationModel?.key ?? DEFAULT_GENERATION_MODEL_KEY,
    parentPremiseRunId: run.id,
    selectedPremise,
    productId: run.productId,
    productVersion: run.productVersion,
    market: run.market,
    angle: toEphemeral(run.angle),
    avatar: toEphemeral(run.avatar),
    problemMechanism: toEphemeral(run.problemMechanism),
    solutionMechanism: toEphemeral(run.solutionMechanism),
    ingredients: run.ingredients.map(toEphemeral),
    awarenessLevel: toEphemeral(run.awarenessLevel),
    ...(run.landingPageUrl ? { landingPageUrl: { ephemeralValue: run.landingPageUrl } } : {}),
    ...(run.optionalGuidance?.trim() ? { optionalGuidance: run.optionalGuidance } : {}),
  } as CreateGenerationQueueItemRequest['brief'];
}

export function PremisePacketCards({ run }: PremisePacketCardsProps) {
  const packets = run.structuredOutput?.premisePackets ?? [];
  const [pendingRanks, setPendingRanks] = useState<Set<number>>(() => new Set());

  async function queueDraft(packet: StructuredPremisePacket) {
    setPendingRanks((current) => new Set(current).add(packet.rank));

    try {
      const response = await generationQueueApi.add({
        brief: buildScratchDraftBrief(run, packet),
      });
      useGenerationQueueStore.getState().mergeItem(response.state, response.item);
      useRunStreamStore.getState().bumpRunsListVersion();

      notifications.show({
        color: 'green',
        title: 'Scratch draft queued',
        message: `Premise ${packet.rank} is queued for drafting.`,
        autoClose: 4000,
      });
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Could not queue scratch draft',
        message: error instanceof Error ? error.message : 'Failed to queue the selected premise.',
        autoClose: 6000,
      });
    } finally {
      setPendingRanks((current) => {
        const next = new Set(current);
        next.delete(packet.rank);
        return next;
      });
    }
  }

  return (
    <Stack
      gap="md"
      p="sm"
      style={{ overflowY: 'auto', flex: '1 1 0', minHeight: 0, maxHeight: '100%' }}
      data-testid="premise-packet-list"
    >
      {packets.map((packet) => (
        <Card
          key={`${packet.rank}-${packet.title}`}
          withBorder
          radius="sm"
          p="sm"
          style={{ overflow: 'visible' }}
          data-testid="premise-packet-card"
        >
          <Stack gap="xs">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                <Badge color="accent" variant="light" size="sm">
                  P{packet.rank}
                </Badge>
                <Text fw={700} fz="sm" lineClamp={2}>
                  {packet.title}
                </Text>
              </Group>
              <Button
                size="xs"
                color="accent"
                variant="filled"
                loading={pendingRanks.has(packet.rank)}
                onClick={() => void queueDraft(packet)}
              >
                Use this
              </Button>
            </Group>

            <Stack gap={4}>
              <Text fz="sm" style={{ overflowWrap: 'anywhere' }}>
                {packet.hook}
              </Text>
              <Text fz="sm" c="dimmed" style={{ overflowWrap: 'anywhere' }}>
                {packet.synopsis}
              </Text>
              <Text fz="xs" c="dimmed" style={{ overflowWrap: 'anywhere' }}>
                {packet.riskToAvoid}
              </Text>
            </Stack>
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
